import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

const EMPTY = { name: "", email: "", role: "user", projects: [] };

/** Admin-created account: emails the invitee a single-use link to choose their password. */
export function AddUserDialog({ projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const toggleProject = (name) =>
    setForm((f) => ({
      ...f,
      projects: f.projects.includes(name) ? f.projects.filter((p) => p !== name) : [...f.projects, name],
    }));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/users/invite", form);
      toast.success(data.message, { description: data.email });
      setForm(EMPTY);
      setOpen(false);
      onCreated();
    } catch (e) {
      toast.error("Could not create the account", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="add-user-btn" className="k-btn-primary">
        <UserPlus className="h-3 w-3" /> Add user
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-user-dialog">
      <div className="absolute inset-0 bg-[#212121]/30" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-lg k-card p-6 bg-white" style={{ animation: "fadeUp .2s ease" }}>
        <div className="k-label">New account</div>
        <h2 className="mt-1 text-2xl font-heading font-bold text-[#212121]">Add a user</h2>
        <p className="mt-2 text-sm text-[#6B7280]">
          They will be emailed a single-use link to set their own password. It expires in 72 hours.
        </p>

        <div className="mt-5 space-y-4">
          <div>
            <label className="k-sublabel">Full name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="add-user-name"
              className="k-input mt-1"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="k-sublabel">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="add-user-email"
              className="k-input mt-1"
              placeholder="jane@company.com"
            />
          </div>
          <div>
            <label className="k-sublabel">Role</label>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              data-testid="add-user-role"
              className="k-input mt-1"
            >
              <option value="user">User — only the projects you tick below</option>
              <option value="admin">Admin — the whole portfolio and settings</option>
            </select>
          </div>
          {form.role === "user" && (
            <div>
              <label className="k-sublabel">Project access</label>
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                {projects.map((p) => (
                  <label key={p.name} className="flex items-center gap-2 text-sm text-[#4D4D4D] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.projects.includes(p.name)}
                      onChange={() => toggleProject(p.name)}
                      data-testid={`add-user-project-${p.name}`}
                    />
                    {p.dis || p.name}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={() => setOpen(false)} className="k-btn-outline">Cancel</button>
          <button
            onClick={submit}
            disabled={busy || !form.name.trim() || !form.email.trim()}
            data-testid="add-user-submit"
            className="k-btn-primary"
          >
            {busy ? "Sending…" : "Create and send invite"}
          </button>
        </div>
      </div>
    </div>
  );
}
